/** Public readiness checks, authored independently of the private assessment.
 * These inspect only requirements already stated in the brief and the learner's
 * own tests. They never complete a session or award a badge.
 */
export const CHECKPOINT_BRANCHES = {
    'cp-first-commits': 'fix/LAT-2312', 'cp-remotes': 'feature/LAT-2471',
    'cp-recovery': 'feature/gift-message', 'cp-quality': 'fix/LAT-2551',
    'cp-team-git': 'feature/LAT-2604', 'cp-code-review': 'feature/LAT-2711',
    'cp-the-codebase': 'refactor/LAT-2733', 'cp-releases': 'hotfix/0.6.1',
    'cp-production': 'fix/LAT-2799', 'cp-pipeline': 'pipeline/LAT-2820',
    'cp-data': 'data/LAT-2840', 'cp-design': 'design/LAT-2860',
    'cp-ownership': 'own/LAT-2880', 'cp-network': 'net/LAT-2900',
    'cp-database': 'db/LAT-2960', 'cp-api': 'api/LAT-3020',
    'cp-security': 'sec/LAT-2991', 'cp-concurrency': 'rush/LAT-3011',
    'cp-observability': 'obs/LAT-3031', 'cp-scale-and-cost': 'scale/LAT-3053',
    'cp-legacy': 'legacy/LAT-3093', 'cp-configuration': 'config/LAT-3115',
    'cp-craft': 'craft/LAT-3143', 'cp-engineer': 'eng/LAT-3171',
    'cp-whole-job': 'job/LAT-3185',
    // The efficiency benchmark (docs/efficiency-bench.md): the same three public
    // checks, on the branch each brief names.
    'bench-a1': 'fix/LAT-4102', 'bench-a2': 'feature/LAT-4111',
    'bench-a3': 'refactor/LAT-4121', 'bench-a4': 'fix/LAT-4131',
    'bench-b1': 'fix/LAT-4202', 'bench-b2': 'feature/LAT-4211',
    'bench-b3': 'refactor/LAT-4221', 'bench-b4': 'fix/LAT-4231',
};
export async function checkCheckpointPractice(ws, mission) {
    const branch = CHECKPOINT_BRANCHES[mission.id];
    if (!branch)
        throw new Error('No public checks for this checkpoint');
    const objectives = [];
    const add = (id, passed) => {
        const objective = mission.objectives.find(o => o.id === id);
        if (!objective)
            throw new Error(`Missing public objective: ${id}`);
        objectives.push({ id, title: objective.title, passed, hidden: false });
    };
    const head = await ws.exec('git symbolic-ref --quiet --short HEAD', { cwd: 'storefront' });
    add('on-branch', head.code === 0 && head.stdout.trim() === branch);
    let output = '';
    // The database checkpoint has no Node suite requirement. The engineer's
    // repository documents a build before tests; perform it only in the copy.
    if (mission.id !== 'cp-database') {
        const suite = await ws.exec(`t=$(mktemp -d) || exit 1
trap 'rm -rf -- "$t"' EXIT
cp -R . "$t/work" && chmod -R u+w "$t/work" && cd "$t/work" || exit 1
${mission.id === 'cp-engineer' ? 'bash tools/build-catalog.sh || exit 1' : ''}
node --test --test-concurrency=1 > "$t/output" 2>&1
r=$?
tail -c 6000 "$t/output"
test "$r" = 0 && ! grep -qE '^# tests 0$' "$t/output"`, { cwd: 'storefront', timeoutMs: 60_000 });
        add(mission.id === 'cp-recovery' ? 'tests-pass' : 'suite', suite.code === 0);
        if (suite.code !== 0)
            output = (suite.stdout + suite.stderr).slice(-6000);
    }
    // Run this even when the tests fail: unfinished work still gets feedback.
    const clean = await ws.exec('git status --porcelain --untracked-files=all', { cwd: 'storefront' });
    add('tidy', clean.code === 0 && clean.stdout.trim() === '');
    const ready = objectives.every(o => o.passed);
    return { grade: { passed: false, objectives, gradedAt: Date.now(), practice: true }, ready, output };
}
//# sourceMappingURL=checkpoint-practice.js.map